/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import {
  getDiagnosticConfig,
  getDiagnosticQuestions,
  getHealth,
  type ReportRequest,
  scoreDiagnosticAnswer,
  type ScoreRequest,
  writeDiagnosticReport,
} from "@/lib/api/generated";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

/**
 * The interview content (tracks, levels, questions, scoring rules).
 *
 * This is static editorial data served by the backend, so it never goes
 * stale within a session: fetch it once and keep it for the whole visit.
 */
export function useDiagnosticQuestions() {
  return useQuery({
    queryKey: ["diagnostic", "questions"],
    queryFn: async () => {
      const { data } = await getDiagnosticQuestions();
      return data;
    },
    staleTime: Infinity,
  });
}

/**
 * The non-secret runtime settings the browser needs: the GA4 measurement id,
 * the HubSpot portal and whether HubSpot tracking is on.
 *
 * Deployment configuration, so it cannot change within a visit: fetched once
 * and kept for the whole session, exactly like the questions above.
 */
export function useDiagnosticConfig() {
  return useQuery({
    queryKey: ["diagnostic", "config"],
    queryFn: async () => {
      const { data } = await getDiagnosticConfig();
      return data;
    },
    staleTime: Infinity,
  });
}

/**
 * How long a typed answer may wait for the model before the chip score wins.
 *
 * The visitor is staring at a typing indicator for the whole wait, so the cap
 * is short: past this the answer is scored locally and the interview moves on.
 */
const SCORE_TIMEOUT_MS = 8500;

/**
 * Score one typed answer.
 *
 * Nothing is cached or invalidated: the result belongs to the interview state
 * in the client, not to a query. `AbortSignal.timeout` rides along on the
 * request, so a hanging backend rejects the mutation instead of stalling the
 * interview, and no retry follows a failure.
 */
export function useScoreAnswer() {
  return useMutation({
    mutationFn: async (body: ScoreRequest) => {
      const { data } = await scoreDiagnosticAnswer({
        body,
        signal: AbortSignal.timeout(SCORE_TIMEOUT_MS),
      });
      return data;
    },
    retry: false,
  });
}

/**
 * How long the gate waits for the written report.
 *
 * The model writes three diagnoses and three moves, which can take the best
 * part of twenty seconds, so this is generous. Past it the visitor is shown a
 * retry rather than an indicator that never resolves.
 */
const REPORT_TIMEOUT_MS = 30_000;

/**
 * Submit the gate and get the written diagnostic back.
 *
 * Nothing is cached or invalidated: the report belongs to the interview state
 * in the client. There is no retry: the endpoint creates a HubSpot lead and
 * is rate limited, so a repeat is the visitor's call, not the query client's.
 */
export function useWriteReport() {
  return useMutation({
    mutationFn: async (body: ReportRequest) => {
      const { data } = await writeDiagnosticReport({
        body,
        signal: AbortSignal.timeout(REPORT_TIMEOUT_MS),
      });
      if (!data) {
        throw new Error("The report response was empty");
      }
      return data;
    },
    retry: false,
  });
}
